/**
 * draftStorage — stockage local des brouillons CR via IndexedDB
 *
 * Correctifs iOS Safari :
 *  - Connexion IDB mise en cache (évite réouverture à chaque save)
 *  - tx.onabort géré (transactions annulées silencieusement sur iOS)
 *  - saveDraftSync() pour sauvegarder du texte de façon synchrone
 *    (utilisé par le handler visibilitychange avant suspension iOS)
 */

const DB_NAME = 'cr_drafts_db';
const DB_VERSION = 1;
const STORE = 'drafts';

// ── Connexion IDB mise en cache ───────────────────────────────────────────────
let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      _dbPromise = null;
      return reject(new Error('IndexedDB non supporté'));
    }
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = e => {
      const db = e.target.result;
      // Réinitialise le cache si la connexion se ferme inopinément (iOS)
      db.onclose = () => { _dbPromise = null; };
      db.onerror  = () => { _dbPromise = null; };
      resolve(db);
    };
    req.onerror = e => {
      _dbPromise = null;
      reject(e.target.error);
    };
  });
  return _dbPromise;
}

/**
 * Sauvegarde un brouillon (cr + dossier) dans IndexedDB.
 * @returns {Promise<true|'fallback'|false>}
 *   true     → IDB OK, photos incluses
 *   'fallback' → localStorage OK, photos retirées (quota IDB dépassé)
 *   false    → échec total
 */
export async function saveDraft(key, cr, dossier) {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ key, cr, dossier, _ts: Date.now() });
      tx.oncomplete = () => resolve(true);
      tx.onerror  = e => { _dbPromise = null; reject(e.target.error); };
      tx.onabort  = ()  => { _dbPromise = null; reject(new Error('IDB transaction aborted')); };
    });
  } catch (e) {
    console.warn('[draftStorage] IndexedDB save failed, fallback localStorage:', e.message);
    try {
      const slim = stripPhotos(cr);
      localStorage.setItem(key, JSON.stringify({ cr: slim, dossier, _ts: Date.now() }));
      return 'fallback';
    } catch (e2) {
      console.error('[draftStorage] localStorage fallback also failed:', e2.message);
      return false;
    }
  }
}

/**
 * Sauvegarde synchrone (texte uniquement, sans photos).
 * Utilisé par le handler visibilitychange sur iOS pour garantir
 * que le texte est persisté avant la suspension du processus JS.
 */
export function saveDraftSync(key, cr, dossier) {
  try {
    const slim = stripPhotos(cr);
    localStorage.setItem(
      key + '_sync',
      JSON.stringify({ cr: slim, dossier, _ts: Date.now() })
    );
  } catch {}
}

/**
 * Charge un brouillon depuis IndexedDB (puis localStorage en fallback).
 * Fusionne avec la sauvegarde synchrone si elle est plus récente.
 * @returns {Promise<{cr, dossier, _ts}|null>}
 */
export async function loadDraft(key) {
  let idbEntry = null;
  try {
    const db = await openDB();
    idbEntry = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = e => resolve(e.target.result || null);
      req.onerror   = e => reject(e.target.error);
    });
  } catch {
    idbEntry = loadFromLocalStorage(key);
  }

  // Vérifier si la sauvegarde synchrone (texte) est plus récente
  const syncEntry = loadFromLocalStorage(key + '_sync');
  if (syncEntry && (!idbEntry || syncEntry._ts > idbEntry._ts + 30000)) {
    // Sync plus récente de plus de 30s → probablement iOS a suspendu avant IDB
    // Fusionner : texte de sync + photos de idbEntry si dispo
    if (idbEntry) {
      return mergeEntries(syncEntry, idbEntry);
    }
    return syncEntry;
  }

  return idbEntry || loadFromLocalStorage(key);
}

/**
 * Supprime un brouillon après envoi/génération PDF.
 */
export async function removeDraft(key) {
  try {
    const db = await openDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror    = e => reject(e.target.error);
    });
  } catch {}
  try { localStorage.removeItem(key); } catch {}
  try { localStorage.removeItem(key + '_sync'); } catch {}
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function loadFromLocalStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * Fusionne deux entrées : prend les champs texte de `newer`
 * et récupère les tableaux photos de `older` (IDB, qui a les photos).
 */
function mergeEntries(newer, older) {
  const merged = { ...newer };
  if (!merged.cr || !older.cr) return merged;
  merged.cr = { ...newer.cr };
  for (const k of Object.keys(older.cr)) {
    const v = older.cr[k];
    // Récupérer les photos de l'entrée IDB
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string' && v[0].startsWith('data:image')) {
      merged.cr[k] = v;
    }
    if (typeof v === 'string' && v.startsWith('data:image') && !merged.cr[k]) {
      merged.cr[k] = v;
    }
  }
  merged._ts = newer._ts;
  return merged;
}

/** Retire les champs photos (arrays de base64) pour le fallback localStorage */
function stripPhotos(cr) {
  if (!cr) return cr;
  const out = { ...cr };
  for (const k of Object.keys(out)) {
    const v = out[k];
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string' && v[0].startsWith('data:image')) {
      out[k] = [];
    }
    if (typeof v === 'string' && v.startsWith('data:image')) {
      out[k] = '';
    }
  }
  return out;
}
